<script setup lang="ts">
import type { MapEditorManuscriptChapter } from "./MapEditor.port";
import StaticSvgMarkup from "./ui/StaticSvgMarkup.vue";

const props = defineProps<{
  chapters: MapEditorManuscriptChapter[];
  /** True when the scene already has a place on the map. */
  isScenePlaced: (path: string) => boolean;
  /** Landmark SVG markup for a placed scene, or empty when free marker / none. */
  landmarkIconMarkup: (path: string) => string;
}>();

const emit = defineEmits<{
  openScene: [path: string];
  /** Pointer drag onto the map (HTML5 DnD is unreliable in the Tauri WebView). */
  startDrag: [payload: { paths: string[]; label: string; event: PointerEvent }];
  /** Paths whose map places should highlight; empty clears. */
  hoverPaths: [paths: readonly string[]];
}>();

function chapterPaths(chapter: MapEditorManuscriptChapter): string[] {
  return chapter.scenes.map((s) => s.path);
}

function isChapterPlaced(chapter: MapEditorManuscriptChapter): boolean {
  const paths = chapterPaths(chapter);
  return paths.length > 0 && paths.every((path) => props.isScenePlaced(path));
}

function onRowPointerDown(
  event: PointerEvent,
  paths: string[],
  label: string,
  placed: boolean,
): void {
  if (placed) return;
  if (paths.length === 0) return;
  emit("startDrag", { paths, label, event });
}

function onRowEnter(paths: readonly string[], placed: boolean): void {
  if (placed === false) {
    emit("hoverPaths", []);
    return;
  }
  emit("hoverPaths", paths);
}

function onRowLeave(): void {
  emit("hoverPaths", []);
}
</script>

<template>
  <aside class="map-story-sidebar" aria-label="Story path">
    <header class="map-story-sidebar-head">
      <h2 class="map-story-sidebar-title">Story path</h2>
    </header>

    <section class="map-story-section">
      <div class="map-story-section-title">Manuscript</div>
      <p v-if="chapters.length === 0" class="map-story-empty">
        No chapters yet.
      </p>
      <ul v-else class="map-story-chapters">
        <li v-for="chapter in chapters" :key="chapter.folder">
          <div
            class="map-story-chapter"
            :class="{ 'map-story-row-placed': isChapterPlaced(chapter) }"
            role="button"
            tabindex="0"
            :title="
              isChapterPlaced(chapter)
                ? chapter.title
                : `Drag all scenes in ${chapter.title}`
            "
            @pointerdown="
              onRowPointerDown(
                $event,
                chapterPaths(chapter),
                chapter.title,
                isChapterPlaced(chapter),
              )
            "
            @pointerenter="
              onRowEnter(chapterPaths(chapter), isChapterPlaced(chapter))
            "
            @pointerleave="onRowLeave"
          >
            {{ chapter.title }}
          </div>
          <ul class="map-story-scenes">
            <li v-for="scene in chapter.scenes" :key="scene.path">
              <div
                class="map-story-scene"
                :class="{ 'map-story-row-placed': isScenePlaced(scene.path) }"
                role="button"
                tabindex="0"
                @pointerdown="
                  onRowPointerDown(
                    $event,
                    [scene.path],
                    scene.label,
                    isScenePlaced(scene.path),
                  )
                "
                @pointerenter="
                  onRowEnter([scene.path], isScenePlaced(scene.path))
                "
                @pointerleave="onRowLeave"
                @click="emit('openScene', scene.path)"
                @keydown.enter.prevent="emit('openScene', scene.path)"
              >
                <StaticSvgMarkup
                  v-if="landmarkIconMarkup(scene.path).length > 0"
                  class="map-story-scene-icon"
                  aria-hidden="true"
                  :markup="landmarkIconMarkup(scene.path)"
                />
                <span class="map-story-scene-label">{{ scene.label }}</span>
              </div>
            </li>
          </ul>
        </li>
      </ul>
    </section>
  </aside>
</template>

<style scoped>
.map-story-sidebar {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem 1rem;
  min-height: 0;
  overflow: auto;
}

.map-story-sidebar-head {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.map-story-sidebar-title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-strong);
}

.map-story-empty {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--text-muted);
}

.map-story-section {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.map-story-section-title {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.map-story-chapters,
.map-story-scenes {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.map-story-scenes {
  padding-left: 0.65rem;
  margin-bottom: 0.35rem;
}

.map-story-chapter,
.map-story-scene {
  box-sizing: border-box;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 0.8125rem;
  padding: 0.3rem 0.4rem;
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.map-story-scene {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.map-story-scene-icon {
  display: inline-flex;
  flex-shrink: 0;
  width: 0.85rem;
  height: 0.85rem;
  color: var(--text-muted);
}

.map-story-scene-icon :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
}

.map-story-scene-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.map-story-chapter:hover,
.map-story-scene:hover {
  background: var(--hover-fill);
  border-color: var(--line);
}

.map-story-row-placed {
  cursor: default;
}

.map-story-chapter {
  font-weight: 600;
}
</style>
